import React, {forwardRef, useEffect, useImperativeHandle, useState} from 'react';
import ReactFlow, {Background, BackgroundVariant, Controls, MiniMap} from 'reactflow';
import {useTranslation} from 'react-i18next';
import 'reactflow/dist/style.css';
import {IAppliedRule} from '../../../../incase-domain/appliedRules';
import SlideNode from './nodes/SlideNode';
import QuestionNode from './nodes/QuestionNode';
import ActionNode from './nodes/ActionNode';
import FlowLegend from './FlowLegend';
import {useFlowState} from './hooks/useFlowState';
import {useConnectionHandlers} from './hooks/useConnectionHandlers';
import {flowToRules} from '../util/flowToRules';

export {PREDICATEABLE_ELEMENTS} from '../util/flowStructure';

const NODE_TYPES = {slide: SlideNode, question: QuestionNode, action: ActionNode};

export interface RulesFlowEditorHandle {
    getRules(): IAppliedRule[];
}

export interface RulesFlowEditorProps {
    initialRules: IAppliedRule[];
    isOpen: boolean;
}

const RulesFlowEditor = forwardRef<RulesFlowEditorHandle, RulesFlowEditorProps>(function RulesFlowEditor({initialRules, isOpen}, ref) {
    const {t} = useTranslation();
    const [isMounted, setIsMounted] = useState(false);

    const {nodes, setNodes, edges, setEdges, allEdges, slides, makeCallbacks, onSelectionChange, onNodesChange, onEdgesChange} =
        useFlowState({initialRules, isOpen});

    const {rfInstance, isValidConnection, onConnect, onConnectStart, onConnectEnd} = useConnectionHandlers({
        nodes,
        setNodes,
        setEdges,
        makeCallbacks,
    });

    useImperativeHandle(
        ref,
        () => ({
            getRules: () => flowToRules(nodes, edges, slides, (i) => `${t('presEditPage.rules.ruleLabel')} [${i + 1}]`),
        }),
        [nodes, edges, slides, t]
    );

    useEffect(() => {
        const id = setTimeout(() => setIsMounted(true), 300);
        return () => clearTimeout(id);
    }, []);

    if (!isMounted) return <div className="rf-editor" />;

    return (
        <div className="rf-editor">
            <ReactFlow
                nodes={nodes}
                edges={allEdges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onConnectStart={onConnectStart}
                onConnectEnd={onConnectEnd}
                onSelectionChange={onSelectionChange}
                isValidConnection={isValidConnection}
                nodeTypes={NODE_TYPES}
                onInit={(instance) => {
                    rfInstance.current = instance;
                }}
                fitView
                fitViewOptions={{padding: 0.2}}
                minZoom={0.2}
                maxZoom={2}
                deleteKeyCode={['Delete', 'Backspace']}
            >
                <Background variant={BackgroundVariant.Dots} gap={16} size={1} color="#d1d5db" />
                <Controls />
                <MiniMap nodeStrokeWidth={3} zoomable pannable />
                <FlowLegend />
            </ReactFlow>
        </div>
    );
});

export default RulesFlowEditor;
